import ConfirmModal from '@/components/confirm-modal'
import LinkAppointmentModal from '@/components/billing/LinkAppointmentModal'
import {
    dismissOrphanPayment,
    getOrphanPayments,
    linkOrphanPayment,
    OrphanPaymentWithSuggestions,
    replaceOrphanPayment,
    OrphanSuggestion,
} from '@/lib/api/financial/orphan-payments-api'
import { notifyOrphanPaymentsChanged } from '@/lib/hooks/use-orphan-payments-count'
import { useServiceLabel } from '@/lib/hooks/use-service-label'
import { useCallback, useEffect, useState } from 'react'
import { MdCallSplit, MdCheckCircle, MdLinkOff, MdRefresh, MdSearch, MdSwapHoriz, MdVerified, MdWarning } from 'react-icons/md'
import { Link } from 'react-router-dom'
import { toast } from 'react-toastify'
import LinkManyModal from './link-many-modal'

const formatCurrency = (cents: number) =>
    new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(cents / 100)

const formatDate = (iso: string | null) =>
    iso ? new Intl.DateTimeFormat('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' }).format(new Date(iso)) : '-'

const MANUAL_METHOD_LABEL: Record<string, string> = { OTHER: 'Outro', PIX: 'Pix' }

/** Cor do card: o match exato (mesmo valor e mesmo dia de uma baixa manual) se destaca. */
const suggestionTone = (s: OrphanSuggestion) =>
    s.exactMatch
        ? 'border-success bg-success/10 ring-1 ring-success/40'
        : s.replace
          ? 'border-info/60 bg-info/5'
          : s.possibleDuplicate
            ? 'border-warning/60 bg-warning/5'
            : 'border-base-300/60'

const APPOINTMENT_PATH: Record<OrphanSuggestion['appointmentType'], string> = {
    EXAM: '/bookings/exams',
    CONSULTATION: '/bookings/consultations',
    PROCEDURE: '/bookings/orders',
}

/**
 * Pagamentos sem vínculo: Pix que entrou na conta fora de qualquer cobrança.
 *
 * Solto, ele faz o atendimento que pagou aparecer com "falta receber" e convida alguém a
 * registrar o mesmo valor em "Outro" — o caixa conta o dinheiro duas vezes. Aqui cada Pix
 * aparece com as sugestões de atendimento, casadas pelo VALOR (quem paga costuma ser outra
 * pessoa: o marido, a empresa), e sai da lista ao ser vinculado ou dispensado.
 *
 * Quando o mesmo valor já foi baixado à mão em "Outro" (ou "Pix") num atendimento, a
 * sugestão vem com "Substituir": o Pix herda tudo da baixa, que é excluída. Vincular
 * ali somaria o dinheiro duas vezes. Mesmo valor e mesmo dia é o match exato, em verde.
 */
export default function OrphanPaymentsPage() {
    const labels = useServiceLabel()
    const typeLabel: Record<OrphanSuggestion['appointmentType'], string> = {
        EXAM: labels.Exam,
        CONSULTATION: labels.Consultation,
        PROCEDURE: labels.Order,
    }

    const [items, setItems] = useState<OrphanPaymentWithSuggestions[]>([])
    const [loading, setLoading] = useState(true)
    const [linkTarget, setLinkTarget] = useState<{ orphan: OrphanPaymentWithSuggestions; suggestion: OrphanSuggestion } | null>(null)
    const [linking, setLinking] = useState(false)
    const [replaceTarget, setReplaceTarget] = useState<{ orphan: OrphanPaymentWithSuggestions; suggestion: OrphanSuggestion } | null>(null)
    const [replacing, setReplacing] = useState(false)
    const [searchTarget, setSearchTarget] = useState<OrphanPaymentWithSuggestions | null>(null)
    const [linkManyTarget, setLinkManyTarget] = useState<OrphanPaymentWithSuggestions | null>(null)
    const [dismissTarget, setDismissTarget] = useState<OrphanPaymentWithSuggestions | null>(null)
    const [dismissNote, setDismissNote] = useState('')
    const [dismissing, setDismissing] = useState(false)

    const load = useCallback(async () => {
        setLoading(true)
        try {
            const response = await getOrphanPayments()
            if (response.success) setItems(response.result ?? [])
            else toast.error(response.error || 'Não foi possível carregar os pagamentos sem vínculo.')
        } finally {
            setLoading(false)
        }
    }, [])

    useEffect(() => {
        load()
    }, [load])

    const resolved = (message: string) => {
        toast.success(message)
        notifyOrphanPaymentsChanged()
        load()
    }

    const confirmLink = async () => {
        if (!linkTarget) return
        setLinking(true)
        try {
            const { orphan, suggestion } = linkTarget
            const response = await linkOrphanPayment(orphan.id, suggestion.appointmentType, suggestion.appointmentId)
            if (response.success) {
                setLinkTarget(null)
                resolved(response.result?.message || 'Pagamento vinculado.')
            } else {
                toast.error(response.error || 'Não foi possível vincular.')
            }
        } finally {
            setLinking(false)
        }
    }

    const confirmReplace = async () => {
        if (!replaceTarget?.suggestion.replace) return
        setReplacing(true)
        try {
            const { orphan, suggestion } = replaceTarget
            const response = await replaceOrphanPayment(
                orphan.id,
                suggestion.appointmentType,
                suggestion.appointmentId,
                suggestion.replace!.billingId,
            )
            if (response.success) {
                setReplaceTarget(null)
                resolved(response.result?.message || 'Baixa manual substituída pelo Pix.')
            } else {
                toast.error(response.error || 'Não foi possível substituir.')
            }
        } finally {
            setReplacing(false)
        }
    }

    const confirmDismiss = async () => {
        if (!dismissTarget) return
        if (dismissNote.trim().length < 5) {
            toast.error('Explique em poucas palavras de onde é este pagamento.')
            return
        }
        setDismissing(true)
        try {
            const response = await dismissOrphanPayment(dismissTarget.id, dismissNote.trim())
            if (response.success) {
                setDismissTarget(null)
                setDismissNote('')
                resolved('Pagamento marcado como "não é de atendimento".')
            } else {
                toast.error(response.error || 'Não foi possível salvar.')
            }
        } finally {
            setDismissing(false)
        }
    }

    return (
        <div className="flex flex-col gap-6">
            <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="flex items-start gap-3">
                    <div className="p-2 rounded-xl bg-error/10 text-error">
                        <MdLinkOff className="text-2xl" />
                    </div>
                    <div>
                        <h1 className="text-2xl font-bold text-base-content">Pagamentos sem vínculo</h1>
                        <p className="text-sm text-base-content/60 max-w-3xl">
                            Pix que entraram na conta fora de qualquer cobrança. Vincule cada um ao atendimento que
                            ele pagou — senão o atendimento fica com &quot;falta receber&quot; e o mesmo valor acaba
                            registrado de novo à mão, contando o dinheiro duas vezes. As sugestões casam pelo valor:
                            quem paga costuma ser outra pessoa. Se o valor já foi baixado à mão em &quot;Outro&quot;,
                            use &quot;Substituir&quot;: o Pix entra no lugar da baixa manual. Um Pix que pagou atendimentos
                            de várias pessoas vai em &quot;Vários atendimentos&quot;.
                        </p>
                    </div>
                </div>
                <button className="btn btn-sm btn-ghost border border-base-300 gap-1" onClick={load} disabled={loading}>
                    <MdRefresh className={`text-base ${loading ? 'animate-spin' : ''}`} />
                    Atualizar
                </button>
            </div>

            {loading && !items.length ? (
                <div className="flex justify-center py-16">
                    <span className="loading loading-spinner loading-lg text-secondary" />
                </div>
            ) : !items.length ? (
                <div className="bg-base-100 rounded-2xl border border-base-300/60 p-10 text-center flex flex-col items-center gap-2">
                    <MdCheckCircle className="text-4xl text-success" />
                    <p className="font-medium">Nenhum pagamento sem vínculo.</p>
                    <p className="text-sm text-base-content/60">Todo Pix recebido está ligado a uma cobrança.</p>
                </div>
            ) : (
                <div className="flex flex-col gap-4">
                    {items.map(orphan => (
                        <div key={orphan.id} className="bg-base-100 rounded-2xl border border-base-300/60 p-5 flex flex-col gap-4">
                            <div className="flex flex-wrap items-start justify-between gap-3">
                                <div>
                                    <p className="text-2xl font-bold text-base-content">{formatCurrency(orphan.amount)}</p>
                                    <p className="text-sm text-base-content/70">
                                        Pix recebido em {formatDate(orphan.paidAt)}
                                        {orphan.companyName ? ` · ${orphan.companyName}` : ''}
                                    </p>
                                    <p className="text-sm mt-1">
                                        <span className="text-base-content/60">Quem pagou: </span>
                                        {orphan.payerName ? (
                                            <span className="font-medium">{orphan.payerName}</span>
                                        ) : (
                                            <span className="italic text-base-content/50">ainda não identificado no extrato</span>
                                        )}
                                    </p>
                                </div>
                                <div className="flex flex-wrap gap-2">
                                    <Link to={`/financial/billings/${orphan.id}`} className="btn btn-sm btn-ghost">
                                        Ver cobrança
                                    </Link>
                                    <button className="btn btn-sm btn-ghost border border-base-300 gap-1" onClick={() => setSearchTarget(orphan)}>
                                        <MdSearch className="text-base" />
                                        Buscar outro atendimento
                                    </button>
                                    <button
                                        className="btn btn-sm btn-ghost border border-base-300 gap-1"
                                        onClick={() => setLinkManyTarget(orphan)}
                                    >
                                        <MdCallSplit className="text-base" />
                                        Vários atendimentos
                                    </button>
                                    <button className="btn btn-sm btn-ghost border border-base-300" onClick={() => setDismissTarget(orphan)}>
                                        Não é de atendimento
                                    </button>
                                </div>
                            </div>

                            {orphan.suggestions.length ? (
                                <div className="flex flex-col gap-2">
                                    <p className="text-xs font-medium uppercase tracking-wide text-base-content/50">Sugestões</p>
                                    {orphan.suggestions.map(s => (
                                        <div
                                            key={`${s.appointmentType}-${s.appointmentId}`}
                                            className={`rounded-xl border p-3 flex flex-wrap items-center justify-between gap-3 ${suggestionTone(s)}`}
                                        >
                                            <div className="flex flex-col gap-1 min-w-0">
                                                {s.exactMatch && (
                                                    <span className="badge badge-success badge-sm gap-1 font-medium">
                                                        <MdVerified className="text-sm" />
                                                        Match exato: mesmo valor e mesmo dia
                                                    </span>
                                                )}
                                                <p className="font-medium truncate">
                                                    <Link
                                                        to={`${APPOINTMENT_PATH[s.appointmentType]}/${s.appointmentId}`}
                                                        className="link link-hover"
                                                        target="_blank"
                                                    >
                                                        {typeLabel[s.appointmentType]} #{s.appointmentId}
                                                    </Link>
                                                    {s.clientName ? ` · ${s.clientName}` : ''}
                                                    {s.title ? <span className="text-base-content/60"> · {s.title}</span> : null}
                                                </p>
                                                <p className="text-xs text-base-content/60">
                                                    Vendido em {formatDate(s.saleDate)} · {formatCurrency(s.sale)} · recebido {formatCurrency(s.received)} ·
                                                    falta <span className="font-medium text-base-content/80">{formatCurrency(s.remaining)}</span>
                                                    {s.flex ? ' · flex' : ''}
                                                </p>
                                                {s.replace && (
                                                    <p className="text-xs text-base-content/70">
                                                        Baixa manual em &quot;{MANUAL_METHOD_LABEL[s.replace.paymentMethod ?? ''] ?? 'Outro'}&quot; de{' '}
                                                        <span className="font-medium">{formatCurrency(s.replace.amount)}</span> em{' '}
                                                        {formatDate(s.replace.paidAt)} ·{' '}
                                                        <Link
                                                            to={`/financial/billings/${s.replace.billingId}`}
                                                            className="link link-hover"
                                                            target="_blank"
                                                        >
                                                            cobrança #{s.replace.billingId}
                                                        </Link>
                                                    </p>
                                                )}
                                                <div className="flex flex-wrap gap-1">
                                                    {s.reasons.map(reason => (
                                                        <span
                                                            key={reason}
                                                            className={`badge badge-sm ${reason.startsWith('Atenção') ? 'badge-warning' : 'badge-ghost'}`}
                                                        >
                                                            {reason}
                                                        </span>
                                                    ))}
                                                </div>
                                            </div>
                                            {s.replace ? (
                                                <div className="flex flex-wrap gap-2">
                                                    <button
                                                        className={`btn btn-sm gap-1 ${s.exactMatch ? 'btn-success' : 'btn-info'}`}
                                                        onClick={() => setReplaceTarget({ orphan, suggestion: s })}
                                                    >
                                                        <MdSwapHoriz className="text-base" />
                                                        Substituir
                                                    </button>
                                                    <button
                                                        className="btn btn-sm btn-ghost border border-base-300"
                                                        onClick={() => setLinkTarget({ orphan, suggestion: s })}
                                                    >
                                                        Vincular
                                                    </button>
                                                </div>
                                            ) : (
                                                <button className="btn btn-sm btn-secondary" onClick={() => setLinkTarget({ orphan, suggestion: s })}>
                                                    Vincular
                                                </button>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <p className="text-sm text-base-content/60">
                                    Nenhum atendimento com saldo em aberto ou baixa manual deste valor casa com este Pix. Use &quot;Buscar outro
                                    atendimento&quot; ou, se não for de atendimento, &quot;Não é de atendimento&quot;.
                                </p>
                            )}
                        </div>
                    ))}
                </div>
            )}

            <ConfirmModal
                isOpen={!!linkTarget}
                onClose={() => setLinkTarget(null)}
                onConfirm={confirmLink}
                loading={linking}
                title="Vincular pagamento"
                message={
                    linkTarget
                        ? `Vincular o Pix de ${formatCurrency(linkTarget.orphan.amount)} a ${typeLabel[linkTarget.suggestion.appointmentType]} #${linkTarget.suggestion.appointmentId}${linkTarget.suggestion.clientName ? ` (${linkTarget.suggestion.clientName})` : ''}? Ele passa a contar como recebido deste atendimento.${
                              linkTarget.suggestion.replace
                                  ? ` ATENÇÃO: este atendimento já tem baixa manual de ${formatCurrency(linkTarget.suggestion.replace.amount)}. Vincular soma o Pix como um pagamento a mais — se for o mesmo dinheiro, use "Substituir".`
                                  : linkTarget.suggestion.possibleDuplicate
                                    ? ` ATENÇÃO: já existe uma baixa manual de ${formatCurrency(linkTarget.suggestion.possibleDuplicate.amount)} neste atendimento — se for o mesmo dinheiro, cancele a baixa manual depois de vincular.`
                                    : ''
                          }`
                        : ''
                }
                confirmButtonText="Vincular"
                confirmButtonClass="btn-secondary"
            />

            <ConfirmModal
                isOpen={!!replaceTarget}
                onClose={() => setReplaceTarget(null)}
                onConfirm={confirmReplace}
                loading={replacing}
                title="Substituir baixa manual pelo Pix"
                message={
                    replaceTarget?.suggestion.replace
                        ? `A baixa manual #${replaceTarget.suggestion.replace.billingId} de ${formatCurrency(replaceTarget.suggestion.replace.amount)} (${formatDate(replaceTarget.suggestion.replace.paidAt)}) em ${typeLabel[replaceTarget.suggestion.appointmentType]} #${replaceTarget.suggestion.appointmentId}${replaceTarget.suggestion.clientName ? ` (${replaceTarget.suggestion.clientName})` : ''} será excluída e o Pix de ${formatCurrency(replaceTarget.orphan.amount)} de ${formatDate(replaceTarget.orphan.paidAt)} assume o lugar dela, herdando vínculo, repasses, complementos e notas fiscais. Nada é cancelado ou estornado, e o registro de caixa não é alterado.`
                        : ''
                }
                confirmButtonText="Substituir"
                confirmButtonClass="btn-success"
            />

            {searchTarget && (
                <LinkAppointmentModal
                    billingId={searchTarget.id}
                    billingAmount={searchTarget.amount}
                    onClose={() => setSearchTarget(null)}
                    onLinked={() => resolved('Pagamento vinculado.')}
                />
            )}

            {linkManyTarget && (
                <LinkManyModal
                    orphan={linkManyTarget}
                    typeLabel={typeLabel}
                    onClose={() => setLinkManyTarget(null)}
                    onLinked={message => {
                        setLinkManyTarget(null)
                        resolved(message)
                    }}
                />
            )}

            {dismissTarget && (
                <div className="modal modal-open">
                    <div className="modal-box max-w-lg flex flex-col gap-4">
                        <div className="flex items-start gap-2">
                            <MdWarning className="text-xl text-warning shrink-0 mt-0.5" />
                            <div>
                                <h3 className="font-semibold text-lg">Não é de atendimento</h3>
                                <p className="text-sm text-base-content/60">
                                    O Pix de {formatCurrency(dismissTarget.amount)}
                                    {dismissTarget.payerName ? ` de ${dismissTarget.payerName}` : ''} sai desta lista. Diga de onde
                                    ele é — fica gravado na cobrança.
                                </p>
                            </div>
                        </div>
                        <div className="form-control">
                            <label className="label py-1" htmlFor="orphan-dismiss-note">
                                <span className="label-text font-medium">De onde é este pagamento?</span>
                            </label>
                            <textarea
                                id="orphan-dismiss-note"
                                className="textarea textarea-bordered w-full"
                                rows={3}
                                placeholder="Ex.: fatura do convênio de agosto"
                                value={dismissNote}
                                onChange={e => setDismissNote(e.target.value)}
                            />
                        </div>
                        <div className="modal-action mt-0">
                            <button className="btn btn-ghost" onClick={() => setDismissTarget(null)} disabled={dismissing}>
                                Cancelar
                            </button>
                            <button className="btn btn-secondary" onClick={confirmDismiss} disabled={dismissing}>
                                {dismissing && <span className="loading loading-spinner loading-xs" />}
                                Salvar
                            </button>
                        </div>
                    </div>
                    <div className="modal-backdrop" onClick={() => !dismissing && setDismissTarget(null)} />
                </div>
            )}
        </div>
    )
}
